import React, { ChangeEvent, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { faRobot, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Button from "@/components/button";
import { TextInput } from "@/components/forms";
import UserError from "@/components/user-error";
import { useModal } from "@/hooks/use-modal";
import { anibotMover } from "@/lib/state/actions/tweenbot-actions";
import { animationSelector } from "@/lib/state/selectors/animation-selector";
import { editorSelector } from "@/lib/state/selectors/editor-selector";
import { useAppDispatch } from "@/lib/state/store";

import { ButtonBar, Module } from "../../../layouts/";
import { ButtonGroup } from "../../../layouts/button-group";
import ModalTitle from "../modal-title";

export default function AnibotMoveModal() {
  const dispatch = useAppDispatch();
  const [insertMode, setInsertMode] = useState("insert");
  const [errors, setErrors] = useState<string[]>([]);
  const xAxisRef = useRef<HTMLInputElement>(null);
  const yAxisRef = useRef<HTMLInputElement>(null);
  const numFramesRef = useRef<HTMLInputElement>(null);
  const wrapEdgesRef = useRef<HTMLInputElement>(null);
  const animation = useSelector(animationSelector);
  const editor = useSelector(editorSelector);
  const { closeModal } = useModal();

  const onInsertModeChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setInsertMode(e.currentTarget.value);
  };

  const onSubmit = () => {
    const x = parseInt(xAxisRef.current?.value ?? "0", 10);
    const y = parseInt(yAxisRef.current?.value ?? "0", 10);
    const frameCount = parseInt(numFramesRef.current?.value ?? "10", 10);
    const errorsToShow: string[] = [];

    // Validation
    if (
      insertMode === "replace" &&
      frameCount + editor.currentFrameIndex > animation.sequence.length
    ) {
      errorsToShow.push(
        `Animation is not long enough to move ${frameCount} frames.`,
      );
    }

    if (isNaN(x) || isNaN(y) || isNaN(frameCount)) {
      errorsToShow.push(`Enter valid numbers.`);
    }

    if (errorsToShow.length) {
      return setErrors(errorsToShow);
    } else {
      setErrors([]);
    }

    dispatch(
      anibotMover({
        x: parseInt(xAxisRef.current?.value ?? "0", 10),
        y: parseInt(yAxisRef.current?.value ?? "0", 10),
        frameCount: parseInt(numFramesRef.current?.value ?? "10", 10),
        insertMode,
        wrap: wrapEdgesRef.current?.checked ?? false,
      }),
    );
    closeModal();
  };

  return (
    <div className="text-center">
      <ModalTitle>
        <FontAwesomeIcon icon={faRobot} /> Move Layer
      </ModalTitle>
      <Module>
        <p>
          Move current layer automatically. Use negative numbers to move
          left/up.
        </p>
      </Module>
      <Module>
        <input
          type="radio"
          name="insertMode"
          value="insert"
          id="move-insert"
          checked={insertMode === "insert"}
          onChange={onInsertModeChanged}
        />{" "}
        <label htmlFor="move-insert">Insert new frames</label>{" "}
        <input
          type="radio"
          name="insertMode"
          value="replace"
          id="move-replace"
          checked={insertMode === "replace"}
          onChange={onInsertModeChanged}
        />{" "}
        <label htmlFor="move-replace">Use existing frames</label>
      </Module>
      <Module>
        <input
          type="checkbox"
          id="wrap"
          value="wrap"
          defaultChecked
          ref={wrapEdgesRef}
        />{" "}
        <label htmlFor="wrap">Wrap at edges</label>
      </Module>
      <Module>
        <TextInput
          id="x_axis"
          size={5}
          inline
          label="left/right pixels"
          defaultValue="0"
          type="number"
          className="w-20"
          ref={xAxisRef}
        />
        <TextInput
          id="y_axis"
          size={5}
          inline
          label="up/down pixels"
          defaultValue="0"
          type="number"
          className="w-20"
          ref={yAxisRef}
        />
        <TextInput
          id="frameCount"
          size={5}
          inline
          label="num frames"
          defaultValue="2"
          className="w-20"
          type="number"
          max="100"
          min="2"
          ref={numFramesRef}
        />
        <UserError errors={errors} />
      </Module>
      <ButtonBar>
        <ButtonGroup align="right">
          <Button
            icon={faTimesCircle}
            onClick={closeModal}
            type="negative"
            rightAlign
          >
            Cancel
          </Button>
          <Button
            icon={faRobot}
            onClick={onSubmit}
            type="positive"
            rightAlign
          >
            Run Tweenbot
          </Button>
        </ButtonGroup>
      </ButtonBar>
    </div>
  );
}
